/**
 * Meters & consequences (v0.5 "Consequences", ADR-009) — pure, Phaser-free.
 * Reputation + Heat maintenance (decay, PR-firm recovery), the compounding
 * amplifier that routes rep losses, and the game-over check that the engine
 * turns into a forced prestige. No RNG, no I/O.
 */

import { fireGameOver } from "@/machines/runmeta";
import { METERS } from "@/data/tunables";
import type { GameState } from "@/state/GameState";

export type GameOverReason = "raid" | "shutdown" | "collapse";

/** Rep-loss amplifier when running hot — the compounding death spiral. */
export function repLossMultiplier(state: GameState): number {
  return state.run.heat >= METERS.hotThreshold ? METERS.repLossMultiplier : 1;
}

/**
 * Apply a reputation LOSS (positive magnitude) routed through the two levers:
 * Heat compounding AMPLIFIES it, the PR Firm ABSORBS a capped fraction of it.
 */
export function applyRepLoss(state: GameState, magnitude: number): void {
  if (magnitude <= 0) return;
  const cut = Math.min(METERS.prRepLossCutCap, METERS.prRepLossCutPerLevel * state.run.prFirmLevel);
  const net = magnitude * repLossMultiplier(state) * (1 - cut);
  state.run.reputation -= net;
}

/** Add Heat (broker / death / aggressive discharge). Decay handles removal. */
export function applyHeatGain(state: GameState, amount: number): void {
  if (amount <= 0) return;
  state.run.heat += amount;
}

/**
 * Per-tick meter maintenance: Heat decays (natural + Compliance Officer), and
 * the PR Firm slowly recovers NEGATIVE reputation back toward 0. Mutates run.
 */
export function meterUpkeep(state: GameState): number {
  const run = state.run;
  const decay = METERS.naturalHeatDecay + METERS.complianceHeatDecayPerLevel * run.complianceOfficerLevel;
  run.heat = Math.max(0, run.heat - decay);
  if (run.reputation < 0 && run.prFirmLevel > 0) {
    run.reputation = Math.min(0, run.reputation + METERS.prRepRecoverPerLevel * run.prFirmLevel);
  }
  return applyFixerUpkeep(state); // the bribe paid this tick — the engine logs it as a net expense
}

/**
 * v0.15 §8.2 the Fixer, UPKEEP-SALARY model: a per-tick bribe scaling with the
 * DEA it removes, drawn from funding BEFORE the mitigation applies. If funding
 * can't cover the full bribe, you buy PROPORTIONALLY less safety that tick —
 * out-earn the bribe and DEA climbs to a raid (G4). DEA stays sticky otherwise
 * (§8.4: zero natural decay). Mutates run.funding + run.dea. Returns the bribe
 * PAID this tick so the caller can log it as a net expense (net/sec honesty).
 */
export function applyFixerUpkeep(state: GameState): number {
  const run = state.run;
  if (run.fixerLevel <= 0 || run.dea <= 0) return 0;
  // Never pay to remove more DEA than exists: the per-tick capacity is
  // fixerLevel · perLevel, capped at the DEA on the board — you buy exactly the
  // safety you need (§8.2), no overcharge at the low-DEA boundary.
  const wanted = Math.min(run.fixerLevel * METERS.fixerDeaMitigatePerLevel, run.dea);
  const bribe = wanted * METERS.fixerBribePerDeaPoint;
  const paid = Math.min(Math.max(0, run.funding), bribe);
  const fraction = bribe > 0 ? paid / bribe : 0; // proportional safety when underfunded
  run.funding -= paid;
  run.dea = Math.max(0, run.dea - wanted * fraction);
  return paid;
}

// Danger bands moved to the dangerBands leaf (shared with the RunMeta
// machine, cycle-free); re-exported here for existing consumers.
export { deaDanger, heatDanger } from "@/systems/dangerBands";

/**
 * Game-over? DEA ceiling = federal raid (harshest, checked first); Heat ceiling =
 * state shutdown; Rep floor = reputation collapse.
 */
export function checkGameOver(state: GameState): GameOverReason | null {
  // v0.10 M4: the RunMeta machine arbitrates the ending (same ceilings,
  // same RULED priority — the ceilings + ordering live in fireGameOver (runmeta.ts); thresholds still come from METERS
  // in the machine's arbiter).
  return fireGameOver(state);
}
